import { useT } from "../lib/i18n.jsx";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api.js";
import { IconArrow, IconCheck, IconX } from "../components/icons.jsx";

function fmtPrice(cents, currency = "EUR") {
  if (cents == null) return null;
  const amt = (cents / 100).toFixed(2).replace(/\.00$/, "");
  return currency === "EUR" ? `${amt} €` : `${amt} ${currency}`;
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toISOString().slice(0, 10);
}

export default function BillingPage() {
  const { t } = useT();
  const [quota, setQuota] = useState(null);
  const [plans, setPlans] = useState(null);
  const [packs, setPacks] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [config, setConfig] = useState(null);
  const [buying, setBuying] = useState(null);
  const [status, setStatus] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  // Read ?status= from query on mount (after redirect from Stripe/PayPal)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get("status");
    const provider = params.get("provider");
    const reason = params.get("reason");
    if (s === "success") {
      setStatus({ kind: "ok", message: `Payment via ${provider || "provider"} completed. It may take a moment to reflect.` });
      // Clean URL
      navigate("/billing", { replace: true });
    } else if (s === "cancel") {
      setStatus({ kind: "warn", message: "Checkout was canceled." });
      navigate("/billing", { replace: true });
    } else if (s === "error") {
      setStatus({ kind: "err", message: `Payment error${reason ? ": " + reason : ""}` });
      navigate("/billing", { replace: true });
    }
  }, [location.search, navigate]);

  async function loadAll() {
    const [q, p, sp, sub, pur, cfg] = await Promise.all([
      api("/billing/me/quota"),
      api("/billing/plans").then((r) => r.plans),
      api("/billing/slot-packs").then((r) => r.packs),
      api("/billing/me/subscriptions").then((r) => r.subscriptions).catch(() => []),
      api("/billing/me/purchases").then((r) => r.purchases).catch(() => []),
      api("/billing/config"),
    ]);
    setQuota(q);
    setPlans(p);
    setPacks(sp);
    setSubscriptions(sub);
    setPurchases(pur);
    setConfig(cfg);
  }

  useEffect(() => {
    loadAll().catch((err) => setStatus({ kind: "err", message: err.message }));
    const id = setInterval(loadAll, 20000);
    return () => clearInterval(id);
  }, []);

  async function startSubCheckout(plan, interval, provider) {
    setBuying(`${plan.id}-${interval}`);
    try {
      const res = await api("/billing/checkout/subscription", {
        method: "POST",
        body: { plan_id: plan.id, interval, provider },
      });
      window.location.href = res.redirect_url;
    } catch (err) {
      setStatus({ kind: "err", message: err.message });
      setBuying(null);
    }
  }

  async function startPackCheckout(pack, provider) {
    setBuying(`pack-${pack.id}`);
    try {
      const res = await api("/billing/checkout/slot-pack", {
        method: "POST",
        body: { pack_id: pack.id, provider },
      });
      window.location.href = res.redirect_url;
    } catch (err) {
      setStatus({ kind: "err", message: err.message });
      setBuying(null);
    }
  }

  async function cancelSub(subId) {
    if (!confirm("Cancel this subscription? Your plan will be downgraded immediately and any excess devices will be disabled.")) {
      return;
    }
    try {
      await api(`/billing/me/subscriptions/${subId}/cancel`, { method: "POST" });
      setStatus({ kind: "ok", message: "Subscription canceled." });
      await loadAll();
    } catch (err) {
      setStatus({ kind: "err", message: err.message });
    }
  }

  if (!quota) return <div className="loading">Loading billing</div>;

  return (
    <>
      <div className="ph">
        <div className="ph-title">
          <div className="eyebrow">Account</div>
          <h1 className="h1">Billing &amp; quotas</h1>
        </div>
        <div className="ph-meta">
          <div>Current plan</div>
          <div style={{ marginTop: 4 }}><strong>{quota.plan_label}</strong></div>
        </div>
      </div>

      <AnimatePresence>
        {status && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{ marginBottom: 18 }}
          >
            <StatusBanner status={status} onClose={() => setStatus(null)} />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="section">
        <div className="section-head">
          <h2>Your usage</h2>
          <span className="chip acc">{quota.plan_code}</span>
        </div>
        <div className="section-body">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
            <Meter
              label="Device slots"
              used={quota.devices_used}
              total={quota.device_slots}
              extra={quota.extra_device_slots}
            />
            <Meter
              label="Viewer slots"
              used={0}
              total={quota.viewer_slots}
              extra={quota.extra_viewer_slots}
              note="parallel viewers"
            />
          </div>
        </div>
      </div>

      {/* Active subscriptions */}
      {subscriptions && subscriptions.length > 0 && (
        <div className="section">
          <div className="section-head">
            <h2>Subscriptions</h2>
          </div>
          <div className="section-body" style={{ padding: 0 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Provider</th>
                  <th>Interval</th>
                  <th>Status</th>
                  <th>Renews</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((s) => (
                  <tr key={s.id}>
                    <td>{s.plan_name || s.plan_code}</td>
                    <td><span className="mono">{s.provider}</span></td>
                    <td><span className="chip">{s.interval_unit}</span></td>
                    <td>
                      <span className={"chip " + statusTone(s.status)}>{s.status}</span>
                    </td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      {fmtDate(s.current_period_end)}
                    </td>
                    <td>
                      {(s.status === "active" || s.status === "past_due") && (
                        <button className="btn ghost danger" onClick={() => cancelSub(s.id)}>
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Plans */}
      {plans && plans.length > 0 && (
        <div className="section">
          <div className="section-head">
            <h2>Plans</h2>
          </div>
          <div className="section-body">
            {config && !config.stripe_enabled && !config.paypal_enabled && (
              <div className="error-line" style={{ marginBottom: 18 }}>
                No payment provider is enabled. An admin needs to configure Stripe or PayPal.
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
              {plans.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                  className="card"
                  style={{ padding: 22 }}
                >
                  <div>
                    <div className="card-name">{p.name}</div>
                    <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                      {p.is_free && <span className="chip">free</span>}
                      {p.has_stripe ? <span className="chip acc">stripe</span> : null}
                      {p.has_paypal ? <span className="chip acc">paypal</span> : null}
                    </div>
                  </div>
                  {p.description && (
                    <p style={{ fontSize: 13, color: "var(--text-mute)", margin: 0 }}>
                      {p.description}
                    </p>
                  )}
                  <div className="chips">
                    <span className="chip">{p.device_slots} devices</span>
                    <span className="chip">{p.viewer_slots} viewers</span>
                  </div>
                  <div style={{ marginTop: "auto", paddingTop: 8, borderTop: "1px dashed var(--line)" }}>
                    {p.is_free ? (
                      <div className="mono" style={{ fontSize: 12, color: "var(--text-faint)" }}>
                        Free tier — no payment needed
                      </div>
                    ) : (
                      <PriceAndBuy
                        plan={p}
                        config={config}
                        buying={buying}
                        onBuy={startSubCheckout}
                      />
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Slot Packs */}
      {packs && packs.length > 0 && (
        <div className="section">
          <div className="section-head"><h2>Slot packs</h2></div>
          <div className="section-body">
            <p className="dim" style={{ fontSize: 13, marginTop: 0 }}>
              One-time purchases that permanently add slots to your account.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12, marginTop: 14 }}>
              {packs.map((pk) => (
                <div key={pk.id} className="card" style={{ padding: 18 }}>
                  <div className="card-name">{pk.name}</div>
                  <div className="chips">
                    {pk.device_slots > 0 && <span className="chip">+{pk.device_slots} devices</span>}
                    {pk.viewer_slots > 0 && <span className="chip">+{pk.viewer_slots} viewers</span>}
                  </div>
                  <div className="mono" style={{ fontSize: 16, fontWeight: 600, marginTop: 6 }}>
                    {fmtPrice(pk.price_cents, pk.currency)}
                  </div>
                  <PackBuy
                    pack={pk}
                    config={config}
                    buying={buying}
                    onBuy={startPackCheckout}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Purchase history */}
      {purchases && purchases.length > 0 && (
        <div className="section">
          <div className="section-head"><h2>Purchase history</h2></div>
          <div className="section-body" style={{ padding: 0 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Provider</th>
                  <th>Amount</th>
                  <th>Slots</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((p) => (
                  <tr key={p.id}>
                    <td className="mono" style={{ fontSize: 11 }}>{fmtDate(p.created_at)}</td>
                    <td><span className="mono">{p.provider}</span></td>
                    <td className="mono">{fmtPrice(p.amount_cents, p.currency)}</td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      {p.device_slots_added > 0 && `+${p.device_slots_added}d `}
                      {p.viewer_slots_added > 0 && `+${p.viewer_slots_added}v`}
                    </td>
                    <td>
                      <span className={"chip " + statusTone(p.status)}>{p.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Discord */}
      <DiscordSection />
    </>
  );
}

function PriceAndBuy({ plan, config, buying, onBuy }) {
  const [interval, setInterval_] = useState("month");

  const price =
    interval === "month" ? plan.price_monthly_cents : plan.price_yearly_cents;

  const stripeOk = config?.stripe_enabled && plan.has_stripe;
  const paypalOk = config?.paypal_enabled && plan.has_paypal;
  const buyKey = `${plan.id}-${interval}`;
  const isBusy = buying === buyKey;

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <button
          className={"btn " + (interval === "month" ? "primary" : "ghost")}
          style={{ flex: 1, padding: "6px 10px" }}
          onClick={() => setInterval_("month")}
        >
          Monthly
        </button>
        {plan.price_yearly_cents != null && (
          <button
            className={"btn " + (interval === "year" ? "primary" : "ghost")}
            style={{ flex: 1, padding: "6px 10px" }}
            onClick={() => setInterval_("year")}
          >
            Yearly
          </button>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <span className="mono" style={{ fontSize: 11, color: "var(--text-faint)" }}>
          {interval === "month" ? "per month" : "per year"}
        </span>
        <span className="mono" style={{ fontSize: 18, fontWeight: 600 }}>
          {fmtPrice(price, plan.currency) || "—"}
        </span>
      </div>
      {stripeOk && (
        <button
          className="btn primary full"
          disabled={isBusy}
          onClick={() => onBuy(plan, interval, "stripe")}
        >
          {isBusy ? "Redirecting…" : "Pay with Stripe"} <IconArrow size={12} />
        </button>
      )}
      {paypalOk && (
        <button
          className="btn full"
          disabled={isBusy}
          style={{ marginTop: 6 }}
          onClick={() => onBuy(plan, interval, "paypal")}
        >
          {isBusy ? "Redirecting…" : "Pay with PayPal"} <IconArrow size={12} />
        </button>
      )}
      {!stripeOk && !paypalOk && (
        <div className="mono" style={{ fontSize: 10.5, color: "var(--text-faint)" }}>
          No provider has this plan synced yet.
        </div>
      )}
    </div>
  );
}

function PackBuy({ pack, config, buying, onBuy }) {
  const stripeOk = config?.stripe_enabled && pack.has_stripe;
  const paypalOk = config?.paypal_enabled && pack.has_paypal;
  const isBusy = buying === `pack-${pack.id}`;

  return (
    <div style={{ marginTop: 8 }}>
      {stripeOk && (
        <button
          className="btn primary full"
          disabled={isBusy}
          onClick={() => onBuy(pack, "stripe")}
        >
          {isBusy ? "Redirecting…" : "Buy with Stripe"} <IconArrow size={12} />
        </button>
      )}
      {paypalOk && (
        <button
          className="btn full"
          disabled={isBusy}
          style={{ marginTop: 6 }}
          onClick={() => onBuy(pack, "paypal")}
        >
          {isBusy ? "Redirecting…" : "Buy with PayPal"} <IconArrow size={12} />
        </button>
      )}
      {!stripeOk && !paypalOk && (
        <div className="mono" style={{ fontSize: 10.5, color: "var(--text-faint)", marginTop: 8 }}>
          No provider configured for this pack.
        </div>
      )}
    </div>
  );
}

function Meter({ label, used, total, extra, note }) {
  const pct = total ? Math.min(100, (used / total) * 100) : 0;
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
        <span style={{ fontFamily: "var(--sans)", fontSize: 36, fontWeight: 600, letterSpacing: "-0.04em" }}>
          {used}
        </span>
        <span className="mono" style={{ fontSize: 14, color: "var(--text-dim)" }}>
          / {total}
        </span>
      </div>
      <div style={{ marginTop: 10, height: 4, background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 2, overflow: "hidden" }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: pct + "%" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          style={{
            height: "100%",
            background: pct > 90 ? "var(--err)" : pct > 70 ? "var(--warn)" : "var(--acc)",
            boxShadow: pct > 0 ? "0 0 8px currentColor" : "none",
          }}
        />
      </div>
      <div className="mono" style={{ fontSize: 10.5, color: "var(--text-faint)", marginTop: 8 }}>
        {extra > 0 ? `+${extra} from purchases` : note || "base plan"}
      </div>
    </div>
  );
}

function StatusBanner({ status, onClose }) {
  const bg =
    status.kind === "ok"
      ? "rgba(74, 222, 128, 0.08)"
      : status.kind === "warn"
      ? "rgba(251, 191, 36, 0.08)"
      : "rgba(248, 113, 113, 0.08)";
  const border =
    status.kind === "ok"
      ? "rgba(74, 222, 128, 0.3)"
      : status.kind === "warn"
      ? "rgba(251, 191, 36, 0.3)"
      : "rgba(248, 113, 113, 0.3)";
  const color =
    status.kind === "ok"
      ? "var(--ok)"
      : status.kind === "warn"
      ? "var(--warn)"
      : "var(--err)";
  return (
    <div
      style={{
        padding: "12px 16px",
        background: bg,
        border: `1px solid ${border}`,
        borderLeft: `2px solid ${color}`,
        borderRadius: "var(--r)",
        color,
        fontFamily: "var(--mono)",
        fontSize: 12,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span>{status.message}</span>
      <button onClick={onClose} style={{ background: "transparent", border: "none", color, cursor: "pointer", display: "flex" }}>
        <IconX size={14} />
      </button>
    </div>
  );
}

function statusTone(s) {
  if (s === "active" || s === "paid") return "ok";
  if (s === "past_due" || s === "pending") return "warn";
  if (s === "canceled" || s === "failed" || s === "refunded") return "err";
  return "";
}

function DiscordSection() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  async function load() {
    try {
      const r = await api("/discord/status");
      setStatus(r);
    } catch {
      setStatus({ linked: false, oauth_enabled: false });
    }
  }

  useEffect(() => { load(); }, []);

  async function connect() {
    window.location.href = "/api/discord/connect";
  }

  async function disconnect() {
    setBusy(true);
    try {
      await api("/discord/disconnect", { method: "POST" });
      setMsg({ ok: "Discord disconnected." });
      await load();
    } catch (err) {
      setMsg({ error: err.message });
    } finally {
      setBusy(false);
    }
  }

  async function toggleDMs(enabled) {
    setBusy(true);
    try {
      await api("/discord/dm/toggle", { method: "POST", body: { enabled } });
      await load();
    } catch {}
    setBusy(false);
  }

  if (!status) return null;
  if (!status.oauth_enabled && !status.webhook_enabled) return null;

  return (
    <div className="section">
      <div className="section-head">
        <h2>Discord</h2>
        {status.linked && (
          <span className="chip acc" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--ok)", display: "inline-block" }} />
            {status.username}
          </span>
        )}
      </div>
      <div className="section-body">
        {msg?.ok && <div className="chip ok" style={{ marginBottom: 14, padding: "8px 12px", fontSize: 12 }}>{msg.ok}</div>}
        {msg?.error && <div className="error-line" style={{ marginBottom: 14 }}>{msg.error}</div>}

        {!status.linked ? (
          <div>
            <p style={{ fontSize: 13, color: "var(--text-mute)", marginTop: 0 }}>
              Link your Discord account to receive direct messages for important events like logins, new devices, and purchases.
            </p>
            {status.oauth_enabled ? (
              <button className="btn primary" onClick={connect} disabled={busy}>
                Connect Discord account
              </button>
            ) : (
              <div className="mono" style={{ fontSize: 11, color: "var(--text-faint)" }}>
                Discord OAuth2 is not configured. Ask your administrator.
              </div>
            )}
          </div>
        ) : (
          <div>
            <p style={{ fontSize: 13, color: "var(--text-mute)", marginTop: 0 }}>
              Your Discord account <strong style={{ color: "var(--text)" }}>@{status.username}</strong> is connected.
            </p>
            <div className="setting-row" style={{ padding: "12px 0", borderBottom: "1px solid var(--line)" }}>
              <div className="label-block">
                <div className="lbl">Direct messages</div>
                <div className="key">Receive DMs for account events</div>
              </div>
              <div
                className={"toggle " + (status.dm_enabled ? "on" : "")}
                onClick={() => toggleDMs(!status.dm_enabled)}
                role="switch"
                aria-checked={status.dm_enabled}
              >
                <div className="knob" />
              </div>
            </div>
            <button className="btn ghost danger" onClick={disconnect} disabled={busy} style={{ marginTop: 16 }}>
              Disconnect Discord
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
